import React from 'react'

export function prettyJsonValue(value) {
  if (!value) return ''
  if (typeof value === 'string') {
    try { return JSON.stringify(JSON.parse(value), null, 2) } catch { return value }
  }
  try { return JSON.stringify(value, null, 2) } catch { return '' }
}

export function parseCategoryLandingJson(raw, kind, fallback) {
  if (!raw) return { value: fallback, error: '' }
  if (kind === 'array' && Array.isArray(raw)) return { value: raw, error: '' }
  if (kind === 'object' && typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    return { value: raw, error: '' }
  }
  try {
    const parsed = JSON.parse(String(raw))
    if (kind === 'array') {
      return { value: Array.isArray(parsed) ? parsed : fallback, error: Array.isArray(parsed) ? '' : '배열 형식이 아닙니다.' }
    }
    return {
      value: parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback,
      error: parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? '' : '객체 형식이 아닙니다.',
    }
  } catch (e) {
    return { value: fallback, error: e?.message || 'JSON 파싱 오류' }
  }
}

export default function useCategoryLandingEditorState({ jsonMode, fields = [] }) {
  const rawSignature = JSON.stringify(fields.map((field) => field.raw))
  const valueSignature = JSON.stringify(fields.map((field) => field.value))

  const parsedFields = React.useMemo(() => {
    return fields.reduce((acc, field) => {
      acc[field.key] = parseCategoryLandingJson(field.raw, field.kind, field.fallback)
      return acc
    }, {})
  }, [rawSignature, fields])

  const errors = React.useMemo(() => {
    return fields
      .map((field) => parsedFields[field.key]?.error ? `${field.label}: ${parsedFields[field.key].error}` : '')
      .filter(Boolean)
  }, [fields, parsedFields])

  React.useEffect(() => {
    if (jsonMode) return
    fields.forEach((field) => {
      if (typeof field.setRaw === 'function' && field.value !== undefined) {
        field.setRaw(prettyJsonValue(field.value))
      }
    })
  }, [jsonMode, valueSignature, fields])

  const validateJsonFields = React.useCallback(() => {
    if (errors.length > 0) throw new Error(errors.join(' / '))
  }, [errors])

  const applyParsedToValues = React.useCallback(() => {
    validateJsonFields()
    fields.forEach((field) => {
      if (typeof field.setValue === 'function') {
        field.setValue(parsedFields[field.key]?.value)
      }
    })
  }, [fields, parsedFields, validateJsonFields])

  const getResolvedValue = React.useCallback((key) => {
    const field = fields.find((item) => item.key === key)
    if (!field) return undefined
    if (jsonMode) return parsedFields[key]?.value
    if (field.value !== undefined) return field.value
    return parsedFields[key]?.value
  }, [fields, jsonMode, parsedFields])

  const getResolvedValues = React.useCallback(() => {
    return fields.reduce((acc, field) => {
      acc[field.key] = getResolvedValue(field.key)
      return acc
    }, {})
  }, [fields, getResolvedValue])

  return {
    parsedFields,
    errors,
    validateJsonFields,
    applyParsedToValues,
    getResolvedValue,
    getResolvedValues,
  }
}
